import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { TextInput } from "../../../components";
import { PortalButton } from "./portal_button";
import styles from "./invite_attendees_dialog.module.css";
import { Spacer } from "../../spacer";
import { useMediaQuery } from "../../../hooks";
import { isEmail } from "../../../lib/validation/string";

interface Attendee {
  name: string;
  email: string;
  phone: string;
}

interface AttendeeInput extends Attendee {
  key: number;
  error?: string;
}

interface InviteAttendeesDialogProps {
  show: boolean;
  onHide: () => void;
  numberOfAttendees: number;
  invitedAttendees: Attendee[];
}

export function InviteAttendeesDialog({
  show,
  onHide,
  numberOfAttendees,
  invitedAttendees,
}: InviteAttendeesDialogProps) {
  const { device } = useMediaQuery();

  const [showError, setShowError] = useState(false);

  const handleChangeAttendeeInput = useCallback(
    (value: AttendeeInput) =>
      setInputValues((current) => {
        const updated = [...current];
        const index = updated.findIndex((v) => v.key === value.key);
        updated[index] = value;
        return updated;
      }),
    []
  );

  const attendeeInputs = [];
  const intialInputValues: AttendeeInput[] = [];
  for (let i = 0; i < numberOfAttendees - invitedAttendees.length; i++) {
    const component = (
      <div key={i}>
        <div className={styles.inputTitle}>Attendee {i + 1}</div>
        <Spacer size={12} />
        <InviteAttendeeInput
          key={i}
          invitedAttendees={invitedAttendees}
          onChange={handleChangeAttendeeInput}
          showError={showError}
        />
      </div>
    );
    attendeeInputs.push(component);
    intialInputValues.push({ key: i, name: "", email: "", phone: "" });
  }

  const [, setInputValues] = useState(intialInputValues);

  const invitedAttendeesComponent = [];
  for (let i = 0; i < invitedAttendees.length; i++) {
    const { name, email, phone } = invitedAttendees[i];
    const component = (
      <div key={i}>
        <div className={styles.inputTitle}>Attendee {i + 1}</div>
        <Spacer size={12} />
        <div className={styles.attendeeInputsWrapper}>
          <TextInput value={name} disabled />
          <TextInput value={email} disabled />
          <TextInput value={phone} disabled />
        </div>
      </div>
    );
    invitedAttendeesComponent.push(component);
  }

  const handleClickSendInvites = () => {
    setShowError(true);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName={styles.dialog}
      contentClassName={styles.dialogContent}
      centered
      fullscreen="md-down"
      scrollable
    >
      <Modal.Header closeButton>
        <div className={styles.headerText}>Invite Attendees</div>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.title}>Send an invite.</div>
        <Spacer size={device === "mobile" ? 16 : 20} />
        <div>
          Fill in the boxes below to send an email invitation to your attendees
          with details and directions.
        </div>
        <Spacer size={24} />
        <div>
          <div className={styles.sectionTitle}>MESSAGE</div>
          <Spacer size={16} />
          <TextInput placeholder="i.e. Meet me at Premier for our meeting at 0930." />
        </div>
        <Spacer size={24} />
        <div>
          <div className={styles.sectionTitle}>ENTER ATTENDEE DETAILS</div>
          <Spacer size={16} />
          <div className={styles.allAttendeeInputsWrapper}>
            {attendeeInputs}
          </div>
        </div>
        <Spacer size={24} />
        <div>
          <div className={styles.sectionTitle}>INVITED ATTENDEES</div>
          <Spacer size={16} />
          <div className={styles.allAttendeeInputsWrapper}>
            {invitedAttendeesComponent}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.dialogFooter}>
          <PortalButton onClick={handleClickSendInvites}>
            Send invites
          </PortalButton>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

interface InviteAttendeeInputProps {
  key: number;
  invitedAttendees: Attendee[];
  onChange: (value: AttendeeInput) => void;
  showError: boolean;
}

function InviteAttendeeInput({
  key,
  invitedAttendees,
  onChange,
  showError,
}: InviteAttendeeInputProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  let errorText: string | undefined;
  const errors = { name: false, email: false };
  if ((name || email || phone) && showError) {
    if (invitedAttendees.some((a) => a.email === email)) {
      errorText = "This attendee has already been invited.";
      errors.email = true;
    } else if (!isEmail(email) || !email) {
      errorText = "Please fill a valid email.";
      errors.email = true;
    } else if (!name.trim()) {
      errorText = "Please fill name.";
      errors.name = true;
    }
  }

  useEffect(() => {
    onChange({ key, name, email, phone });
  }, [name, email, phone, onChange, key]);

  return (
    <div>
      <div className={styles.attendeeInputsWrapper}>
        <TextInput
          placeholder="Name"
          value={name}
          onChange={(value) => setName(value)}
          invalid={errors.name}
        />
        <TextInput
          placeholder="Email"
          value={email}
          onChange={(value) => setEmail(value)}
          invalid={errors.email}
        />
        <TextInput
          placeholder="Phone Number (optional)"
          value={phone}
          onChange={(value) => setPhone(value)}
        />
      </div>
      {errorText && (
        <div>
          <Spacer size={8} />
          <div className={styles.error}>{errorText}</div>
        </div>
      )}
    </div>
  );
}
